/* eslint-disable import/no-anonymous-default-export */
import axios from "axios";
import host from "./host";

export const axiosInstance = axios.create();

// Interceptor para tratar respostas e erros
axiosInstance.interceptors.response.use(
  function (rs) {
    return rs;
  },
  function (err) {
    if (err?.response?.data?.redirect_to_login) {
      localStorage.removeItem("user");
      localStorage.removeItem('token');
      const redirectRoute = window.location.origin + '/login';
      window.location.href = redirectRoute;
    } else if (err?.response?.data?.error) {
      // Não faça alert aqui, deixe os componentes tratarem
      console.error(err?.response?.data?.error);
    }
    return Promise.reject(err);
  }
);

const path = {
  auth: "/api/auth-users",
  authUser: "/api/auth-users",
  users: "/api/users",
  clients: "/api/clients",
  batches: "/api/batches",
  dashboard: "/api/dashboard",
  attachments: "/api/attachments",
  enums: "/api/enums",
};

export default {
  // Autenticação
  auth: {
    authenticate: body => {
      return axiosInstance.post(`${host}${path.auth}/authenticate-panel/`, body);
    },
    forgotPassword: email => {
      return axiosInstance.post(`${host}${path.auth}/reset-password/?email=${email}`);
    },
    changePassword: body => {
      return axiosInstance.put(`${host}${path.authUser}/change-password`, body);
    },
    changePasswordForced: body => {
      return axiosInstance.put(`${host}${path.authUser}/change-password-forced`, body);
    },
    resetPasswordForced: (body, token) => {
      return axiosInstance.put(`${host}${path.auth}/change-password-forced`, body, { 
        headers: { common: { ['Authorization']: `Bearer ${token}` } } 
      });
    },
    setAuthorizationToken: async (formattedToken) => {
      axiosInstance.defaults.headers.common.Authorization = formattedToken;
      localStorage.setItem('token', formattedToken);
      return;
    },
    removeAuthorizationToken: async () => {
      axiosInstance.defaults.headers.common.Authorization = null;
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      return;
    },
    resetPassword: id => {
      return axiosInstance.post(`${host}${path.auth}/reset-password-painel/${id}`);
    },
    currentUser: () => {
      return axiosInstance.get(`${host}${path.auth}/current-user/`);
    },
  },

  // Usuários
  user: {
    list: (params) => {
      return axiosInstance.get(`${host}${path.users}/list-panel-admins`, { params });
    },
    listAdmin: (params) => {
      return axiosInstance.get(`${host}${path.users}/list-admin`, { params });
    },
    get: id => {
      return axiosInstance.get(`${host}${path.users}/app/${id}`);
    },
    create: body => {
      return axiosInstance.post(`${host}${path.users}/`, body);
    },
    update: (id, body) => {
      return axiosInstance.put(`${host}${path.users}/${id}`, body);
    },
    delete: id => {
      return axiosInstance.delete(`${host}${path.users}/${id}`);
    },
    activate: body => {
      return axiosInstance.post(`${host}${path.users}/activate-painel`, body);
    },
    inactivate: body => {
      return axiosInstance.post(`${host}${path.users}/inactivate-painel`, body);
    },
  },

  // Clientes
  client: {
    list: (params) => {
      return axiosInstance.get(`${host}${path.clients}/`, { params });
    },
    get: id => {
      return axiosInstance.get(`${host}${path.clients}/${id}`);
    },
    create: body => {
      return axiosInstance.post(`${host}${path.clients}`, body);
    },
    update: (id, body) => {
      return axiosInstance.put(`${host}${path.clients}/${id}`, body);
    },
    delete: id => {
      return axiosInstance.delete(`${host}${path.clients}/${id}`);
    },
    activate: id => {
      return axiosInstance.put(`${host}${path.clients}/activate/${id}`);
    },
    inactivate: id => {
      return axiosInstance.put(`${host}${path.clients}/inactivate/${id}`);
    },
  },

  // Batches (Lotes)
  batch: {
    list: (params) => {
      return axiosInstance.get(`${host}${path.batches}`, { params });
    },
    get: id => {
      return axiosInstance.get(`${host}${path.batches}/${id}`);
    },
    create: body => {
      return axiosInstance.post(`${host}${path.batches}`, body);
    },
    update: (id, body) => {
      return axiosInstance.put(`${host}${path.batches}/${id}`, body);
    },
    delete: id => {
      return axiosInstance.delete(`${host}${path.batches}/${id}`);
    },
    statistics: (params) => {
      return axiosInstance.get(`${host}${path.batches}/statistics/summary`, { params });
    },
    generateLabels: (id, quantity = 1) => {
      return axiosInstance.post(
        `${host}${path.batches}/${id}/labels`,
        { quantity },
        { 
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf'
          }
        }
      );
    },
    // Anexos de lotes
    listAttachments: (batchId) => {
      return axiosInstance.get(`${host}${path.attachments}/batch/${batchId}`);
    },
    uploadAttachment: (batchId, formData) => {
      return axiosInstance.post(`${host}${path.attachments}/batch/${batchId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    deleteAttachment: (attachmentId) => {
      return axiosInstance.delete(`${host}${path.attachments}/batch-attachment/${attachmentId}`);
    },
    downloadAttachment: (token) => {
      return `${host}${path.attachments}/download/${token}`;
    },
  },

  // Dashboard
  dashboard: {
    getKpis: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/kpis`, { params });
    },
    getTimeEvolution: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/time-evolution`, { params });
    },
    getTopClientsByBatches: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/top-clients-batches`, { params });
    },
    getTopClientsByDrums: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/top-clients-drums`, { params });
    },
    getDistributionByState: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/distribution-state`, { params });
    },
    getDistributionByWeekday: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/distribution-weekday`, { params });
    },
    getDistributionByHour: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/distribution-hour`, { params });
    },
    getMonthlyComparison: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/monthly-comparison`, { params });
    },
    getAvgDrumsByClient: (params) => {
      return axiosInstance.get(`${host}${path.dashboard}/avg-drums-by-client`, { params });
    },
  },

  // Enums
  enums: {
    list: () => {
      return axiosInstance.get(`${host}${path.enums}/`);
    },
    find: name => {
      return axiosInstance.get(`${host}${path.enums}/${name}`);
    },
  },
};

